import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
  ArrowLeft, Clock, MapPin, Users, Calendar, CheckCircle,
  XCircle, MinusCircle, ExternalLink, ChevronRight, Zap, Building2, AlertTriangle, BarChart2,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { MarginalSeatsWidget } from "../components/MarginalSeatsWidget";
import { ElectionChecklistWidget } from "../components/ElectionChecklistWidget";
import { SAFundingWidget } from "../components/SAFundingWidget";

// SA election: Saturday, March 21, 2026
const ELECTION_DATE = new Date("2026-03-21T08:00:00+09:30");

const SA_LEADERS = [
  {
    id: "peter-malinauskas",
    name: "Peter Malinauskas",
    role: "Premier of SA — seeking re-election",
    party: "Australian Labor Party",
    color: "#E11D48",
    slogan: "Building South Australia's Future",
    elected: 2022,
    electorate: "Croydon",
    highlights: [
      "Revived Adelaide 500 car race (Dec 2022)",
      "Hydrogen and Renewable Energy Act 2023",
      "$15.4B North–South Corridor tunnels",
      "AUKUS naval shipbuilding at Osborne",
    ],
    criticisms: [
      "Housing targets falling behind",
      "Hospital system still under pressure",
      "Budget deficit growing",
    ],
  },
  {
    id: "david-speirs",
    name: "David Speirs",
    role: "Leader of the Opposition — challenging for Premier",
    party: "Liberal Party of Australia",
    color: "#2563EB",
    slogan: "A Fresh Start for South Australia",
    elected: 2014,
    electorate: "Black",
    highlights: [
      "Return SA Budget to surplus",
      "Reject nuclear waste storage in SA",
      "Cost-of-living relief packages",
      "Cut government bureaucracy and red tape",
    ],
    criticisms: [
      "No alternative renewable energy plan",
      "Details on spending cuts unclear",
      "Party still rebuilding from 2022 defeat",
    ],
  },
];

const SA_ISSUES = [
  {
    id: "aukus",
    title: "AUKUS Nuclear Submarines",
    icon: "🛳️",
    color: "#2563EB",
    description: "SA will host Australia's nuclear submarine fleet at the Osborne Naval Shipyard, creating tens of thousands of jobs over decades.",
    labor: { position: "Support", detail: "Champion of the AUKUS program. Malinauskas has been the most vocal state premier backing the submarines, securing billions in federal investment for SA." },
    liberal: { position: "Support", detail: "Speirs supports AUKUS and the submarine program but says Labor is not doing enough to maximise local SA jobs and content." },
    verdict: "Bipartisan support — AUKUS is a rare area of agreement between both parties.",
  },
  {
    id: "hydrogen",
    title: "Hydrogen & Renewable Energy",
    icon: "⚡",
    color: "#10B981",
    description: "SA is already a renewable energy leader and aims to become a global green hydrogen export hub.",
    labor: { position: "Strongly supports", detail: "Landmark Hydrogen and Renewable Energy Act 2023. SA now exceeds 70% renewable generation. Major green hydrogen export projects under development." },
    liberal: { position: "Sceptical", detail: "Speirs questions the economic viability of some hydrogen projects and wants clearer business cases before committing large public funds." },
    verdict: "Labor leads on renewables; Liberals want more caution on public investment.",
  },
  {
    id: "cost-of-living",
    title: "Cost of Living",
    icon: "💰",
    color: "#EAB308",
    description: "Household cost pressures — including power bills, rents, and grocery costs — are the #1 issue for SA voters in 2026.",
    labor: { position: "Relief packages", detail: "Delivered electricity rebates and cost-of-living concession packages. Pointing to Federal Labor's $300 energy rebates as joint relief." },
    liberal: { position: "Cut waste and taxes", detail: "Wingard's shadow budget promises to ease the pressure through payroll tax cuts, reducing public sector waste, and lowering cost of government." },
    verdict: "Both claim they'll deliver relief — voters will judge on credibility.",
  },
  {
    id: "housing",
    title: "Housing Affordability",
    icon: "🏠",
    color: "#E11D48",
    description: "Adelaide's property market has surged. The median house price has risen over 50% since 2020, pricing out first home buyers.",
    labor: { position: "Supply + social housing", detail: "Committed to 1,000 new public housing homes. Housing SA programs underway but behind target. Supporting infill development near transport." },
    liberal: { position: "Planning reform", detail: "Promises to cut red tape in planning approvals, support more greenfield releases, and reform SA's Development Act to increase supply faster." },
    verdict: "Both parties recognise the housing crisis but disagree on government vs. market-led solutions.",
  },
  {
    id: "health",
    title: "Health & Hospitals",
    icon: "🏥",
    color: "#8B5CF6",
    description: "SA's hospital system has struggled with ambulance ramping and long emergency wait times — a recurring election battleground.",
    labor: { position: "Investment + reform", detail: "Invested $1.4B in health under Malinauskas. New Calvary Hospital announcement. But ramping continues to be an issue." },
    liberal: { position: "Accountability & targets", detail: "Speirs has pledged mandatory ambulance ramping targets with consequences, and says Labor has broken their promise to fix ramping within a term." },
    verdict: "Health is the biggest liability for Malinauskas — Liberals see it as their strongest attack line.",
  },
];

const SA_PARTIES = [
  { name: "ALP", full: "Australian Labor Party", color: "#E11D48", seats2022: 25, leader: "Peter Malinauskas", likelihood: "Favourite" },
  { name: "Liberal", full: "Liberal Party of Australia (SA)", color: "#2563EB", seats2022: 17, leader: "David Speirs", likelihood: "Challenger" },
  { name: "SA-BEST", full: "SA-BEST", color: "#EAB308", seats2022: 2, leader: "Frank Pangallo (Upper House)", likelihood: "Minor" },
  { name: "Greens", full: "Australian Greens (SA)", color: "#10B981", seats2022: 2, leader: "Tammy Franks", likelihood: "Balance of power" },
  { name: "Independent", full: "Independent candidates", color: "#94A3B8", seats2022: 1, leader: "Various", likelihood: "Key seats" },
];

const SA_POLLS = [
  { pollster: "Redbridge", date: "Feb 2026", alp: 40, lib: 31, grn: 11, oth: 18, two_pp_alp: 57, two_pp_lib: 43 },
  { pollster: "Essential", date: "Jan 2026", alp: 39, lib: 32, grn: 10, oth: 19, two_pp_alp: 56, two_pp_lib: 44 },
  { pollster: "Newspoll SA", date: "Dec 2025", alp: 41, lib: 30, grn: 11, oth: 18, two_pp_alp: 58, two_pp_lib: 42 },
  { pollster: "Roy Morgan", date: "Nov 2025", alp: 42, lib: 29, grn: 12, oth: 17, two_pp_alp: 59, two_pp_lib: 41 },
];

const SA_TREND = [
  { month: "Nov '25", ALP: 42, Liberal: 29, Greens: 12 },
  { month: "Dec '25", ALP: 41, Liberal: 30, Greens: 11 },
  { month: "Jan '26", ALP: 39, Liberal: 32, Greens: 10 },
  { month: "Feb '26", ALP: 40, Liberal: 31, Greens: 11 },
];

const KEY_DATES = [
  { label: "Last day to enrol", date: "2026-03-03", note: "~18 days before election" },
  { label: "Pre-poll opens", date: "2026-03-09", note: "12 days before election" },
  { label: "Pre-poll closes", date: "2026-03-20", note: "Friday before election" },
  { label: "ELECTION DAY", date: "2026-03-21", note: "8am – 6pm ACDT", highlight: true },
];

import { useCountdown, CountdownBox } from "../components/ElectionCountdown";

export default function SA2026Page() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const countdown = useCountdown(ELECTION_DATE);

  // Scroll to top on mount (navigation doesn't auto-scroll in React Router)
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const bg = isDark ? "#080E1A" : "#F8FAFC";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";
  const borderC = isDark ? "rgba(51,65,85,0.55)" : "rgba(226,232,240,0.85)";
  const shadowMd = isDark ? "0 4px 20px -2px rgba(0,0,0,0.35)" : "0 4px 16px -2px rgba(15,23,42,0.07)";
  const subBg = isDark ? "#0F172A" : "#F8FAFC";

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="sa2026-page">

      {/* Hero */}
      <div
        className="relative overflow-hidden"
        style={{
          background: "linear-gradient(160deg, #0F172A 0%, #1a0810 40%, #0F172A 100%)",
          borderBottom: "1px solid rgba(51,65,85,0.35)",
        }}
      >
        {/* Ambient glow */}
        <div style={{
          position: "absolute", top: 0, right: "20%",
          width: 500, height: 400,
          background: "radial-gradient(ellipse at center, rgba(225,29,72,0.09) 0%, transparent 70%)",
          filter: "blur(50px)", pointerEvents: "none",
        }} />
        <div style={{ height: 3, background: "linear-gradient(90deg, #E11D48, #F43F5E88, transparent)" }} />

        <div className="max-w-5xl mx-auto px-4 py-12 relative">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm font-medium mb-8 transition-all hover:opacity-70"
            style={{ color: "#FACC15", background: "none", border: "none", cursor: "pointer" }}
            data-testid="back-home-btn"
          >
            <ArrowLeft style={{ width: 15, height: 15 }} /> Home
          </button>

          <div className="flex items-center gap-2 mb-5 flex-wrap">
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: "rgba(225,29,72,0.12)", color: "#F43F5E", border: "1px solid rgba(225,29,72,0.22)" }}>
              SA 2026
            </span>
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: "rgba(234,179,8,0.10)", color: "#FACC15", border: "1px solid rgba(234,179,8,0.20)" }}>
              ELECTION GUIDE
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            South Australia<br />State Election 2026
          </h1>
          <p className="text-base mb-8" style={{ color: "#64748B" }}>
            Saturday, March 21, 2026 · House of Assembly + Legislative Council · 47 lower house seats
          </p>

          {/* Countdown */}
          {!countdown.over ? (
            <div>
              <p className="text-xs font-semibold mb-4 tracking-widest uppercase" style={{ color: "#F43F5E" }}>
                Time until polls open
              </p>
              <div className="flex items-end gap-2 sm:gap-4 flex-wrap">
                <CountdownBox value={countdown.days} label="Days" />
                <span className="text-2xl sm:text-3xl font-black text-white mb-7 opacity-30">:</span>
                <CountdownBox value={countdown.hours} label="Hours" />
                <span className="text-2xl sm:text-3xl font-black text-white mb-7 opacity-30">:</span>
                <CountdownBox value={countdown.minutes} label="Mins" />
                <span className="text-2xl sm:text-3xl font-black text-white mb-7 opacity-30">:</span>
                <CountdownBox value={countdown.seconds} label="Secs" />
              </div>
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl"
              style={{ background: "rgba(225,29,72,0.12)", border: "1px solid rgba(225,29,72,0.22)" }}
            >
              <Zap style={{ width: 15, height: 15, color: "#F43F5E" }} />
              <span className="font-semibold text-rose-400">Election Day has arrived!</span>
            </div>
          )}

          {/* Key dates */}
          <div className="flex flex-wrap gap-2.5 mt-10">
            {KEY_DATES.map((d) => (
              <div
                key={d.label}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
                style={{
                  background: d.highlight ? "rgba(225,29,72,0.12)" : "rgba(255,255,255,0.05)",
                  border: `1px solid ${d.highlight ? "rgba(225,29,72,0.25)" : "rgba(51,65,85,0.4)"}`,
                  color: d.highlight ? "#F43F5E" : "#64748B",
                }}
              >
                <Calendar style={{ width: 12, height: 12, flexShrink: 0 }} />
                <span className="font-semibold">{d.label}:</span>
                <span>{d.date}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-12 space-y-16">

        {/* Head to head leaders */}
        <section data-testid="leaders-section">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.20)" }}>
              <Users style={{ width: 18, height: 18, color: "#EAB308" }} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              Head-to-Head: Who's Running for Premier?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {SA_LEADERS.map((leader) => (
              <div
                key={leader.id}
                className="overflow-hidden transition-all duration-200 hover:-translate-y-1"
                style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 18, boxShadow: shadowMd }}
                data-testid={`leader-card-${leader.id}`}
              >
                <div style={{ height: 3, background: `linear-gradient(90deg, ${leader.color}, ${leader.color}66)` }} />
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold mb-1.5 tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>{leader.name}</h3>
                      <span
                        className="text-xs font-semibold px-2.5 py-1 rounded-full text-white"
                        style={{ background: leader.color }}
                      >
                        {leader.party}
                      </span>
                      <p className="text-xs mt-2" style={{ color: textSec }}>{leader.role}</p>
                    </div>
                  </div>

                  <p className="text-sm font-semibold mb-1 italic" style={{ color: leader.color }}>"{leader.slogan}"</p>
                  <p className="text-xs mb-4" style={{ color: textSec }}>
                    Electorate: {leader.electorate} · In parliament since {leader.elected}
                  </p>

                  <div className="mb-3">
                    <p className="text-xs font-semibold mb-2" style={{ color: "#10B981" }}>Key achievements / promises:</p>
                    {leader.highlights.map((h) => (
                      <div key={h} className="flex items-start gap-1.5 mb-1">
                        <CheckCircle style={{ width: 13, height: 13, flexShrink: 0, marginTop: 2, color: "#10B981" }} />
                        <span className="text-xs" style={{ color: text }}>{h}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mb-4">
                    <p className="text-xs font-semibold mb-2" style={{ color: "#F43F5E" }}>Criticisms:</p>
                    {leader.criticisms.map((c) => (
                      <div key={c} className="flex items-start gap-1.5 mb-1">
                        <XCircle style={{ width: 13, height: 13, flexShrink: 0, marginTop: 2, color: "#F43F5E" }} />
                        <span className="text-xs" style={{ color: text }}>{c}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => navigate(`/politicians/${leader.id}`)}
                    className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all hover:-translate-y-0.5 active:scale-[0.98]"
                    style={{
                      background: leader.color,
                      color: "#fff",
                      boxShadow: `0 4px 14px ${leader.color}33`,
                      border: "none",
                      cursor: "pointer",
                    }}
                    data-testid={`view-profile-${leader.id}`}
                  >
                    View Full Profile + Promise Tracker <ChevronRight style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Key Issues */}
        <section data-testid="issues-section">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.20)" }}>
              <AlertTriangle style={{ width: 18, height: 18, color: "#EAB308" }} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              5 Key Issues Deciding the SA Election
            </h2>
          </div>
          <div className="space-y-4">
            {SA_ISSUES.map((issue) => (
              <div
                key={issue.id}
                className="overflow-hidden transition-all duration-200"
                style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd }}
                data-testid={`issue-${issue.id}`}
              >
                <div style={{ height: 2.5, background: `linear-gradient(90deg, ${issue.color}, ${issue.color}55)` }} />
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <span className="text-2xl">{issue.icon}</span>
                    <div className="flex-1">
                      <h3 className="text-base font-bold mb-1 tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>{issue.title}</h3>
                      <p className="text-sm" style={{ color: textSec }}>{issue.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div
                      className="pl-3 py-2 rounded-r-xl"
                      style={{ borderLeft: `3px solid #E11D48`, background: isDark ? "rgba(225,29,72,0.04)" : "rgba(225,29,72,0.02)" }}
                    >
                      <p className="text-xs font-semibold mb-1" style={{ color: "#E11D48" }}>Labor — {issue.labor.position}</p>
                      <p className="text-xs" style={{ color: textSec }}>{issue.labor.detail}</p>
                    </div>
                    <div
                      className="pl-3 py-2 rounded-r-xl"
                      style={{ borderLeft: `3px solid #2563EB`, background: isDark ? "rgba(37,99,235,0.04)" : "rgba(37,99,235,0.02)" }}
                    >
                      <p className="text-xs font-semibold mb-1" style={{ color: "#2563EB" }}>Liberal — {issue.liberal.position}</p>
                      <p className="text-xs" style={{ color: textSec }}>{issue.liberal.detail}</p>
                    </div>
                  </div>
                  <div
                    className="flex items-start gap-2 mt-3 p-3 rounded-xl"
                    style={{ background: isDark ? "rgba(234,179,8,0.06)" : "rgba(234,179,8,0.05)", border: `1px solid rgba(234,179,8,0.14)` }}
                  >
                    <Zap style={{ width: 13, height: 13, flexShrink: 0, marginTop: 1, color: "#EAB308" }} />
                    <p className="text-xs font-medium" style={{ color: textSec }}>{issue.verdict}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── NEW: Am I Ready Checklist ── */}
        <ElectionChecklistWidget />

        {/* Parties in SA */}
        <section data-testid="parties-section">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.20)" }}>
              <Building2 style={{ width: 18, height: 18, color: "#EAB308" }} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              Parties Running in SA 2026
            </h2>
          </div>
          <div
            className="overflow-hidden"
            style={{ border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd }}
          >
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table className="w-full text-sm" style={{ minWidth: 520 }}>
              <thead>
                <tr style={{ background: isDark ? "#0F172A" : "#F8FAFC", borderBottom: `1px solid ${borderC}` }}>
                  {["Party", "Full Name", "Leader", "2022 Seats", "2026 Outlook"].map((h) => (
                    <th key={h} className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider" style={{ color: textSec }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SA_PARTIES.map((p, i) => (
                  <tr
                    key={p.name}
                    style={{ background: i % 2 === 0 ? surface : (isDark ? "rgba(15,23,42,0.4)" : "#F8FAFC") }}
                    data-testid={`party-row-${p.name.toLowerCase().replace(" ", "-")}`}
                  >
                    <td className="py-3 px-4 font-bold border-b text-sm" style={{ color: p.color, borderColor: borderC }}>{p.name}</td>
                    <td className="py-3 px-4 border-b text-xs" style={{ color: textSec, borderColor: borderC }}>{p.full}</td>
                    <td className="py-3 px-4 border-b text-xs" style={{ color: text, borderColor: borderC }}>{p.leader}</td>
                    <td className="py-3 px-4 border-b text-center font-bold" style={{ color: text, borderColor: borderC }}>{p.seats2022}</td>
                    <td className="py-3 px-4 border-b" style={{ borderColor: borderC }}>
                      <span
                        className="text-xs font-semibold px-2.5 py-1 rounded-full"
                        style={{ background: `${p.color}12`, color: p.color, border: `1px solid ${p.color}22` }}
                      >
                        {p.likelihood}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
          <p className="text-xs mt-2" style={{ color: textSec }}>* 2022 results: Labor 25, Liberal 17, SA-BEST 2, Greens 2, Independent 1. 47 total lower house seats.</p>
        </section>

        {/* SA Polls */}
        <section data-testid="sa-polls-section">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.20)" }}>
              <BarChart2 style={{ width: 18, height: 18, color: "#EAB308" }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>SA 2026 Opinion Polls</h2>
              <p className="text-sm" style={{ color: textSec }}>Primary vote estimates — Labor leads comfortably</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            {[{ party: "ALP — Malinauskas", color: "#E11D48", tpp: 57 }, { party: "Liberal — Speirs", color: "#2563EB", tpp: 43 }].map((p) => (
              <div
                key={p.party}
                className="overflow-hidden"
                style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd, padding: "16px 18px" }}
              >
                <div style={{ height: 2, background: `linear-gradient(90deg, ${p.color}, ${p.color}44)`, marginBottom: 12 }} />
                <p className="text-xs font-semibold mb-1" style={{ color: p.color }}>{p.party}</p>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-black" style={{ color: p.color, fontFamily: "Outfit, sans-serif" }}>{p.tpp}%</span>
                  <span className="text-xs" style={{ color: textSec }}>two-party-preferred</span>
                </div>
                <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: isDark ? "rgba(51,65,85,0.4)" : "#E2E8F0" }}>
                  <div className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${p.color}, ${p.color}cc)`, width: `${p.tpp}%` }} />
                </div>
              </div>
            ))}
          </div>

          <div
            style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd, padding: "20px" }}
            data-testid="sa-poll-trend-chart"
          >
            <p className="text-xs font-semibold mb-4 uppercase tracking-wider" style={{ color: textSec }}>Primary Vote Trend — Nov 2025 to Feb 2026</p>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={SA_TREND} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(51,65,85,0.3)" : "#E2E8F0"} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: textSec }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 55]} tick={{ fontSize: 11, fill: textSec }} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(v) => `${v}%`}
                  contentStyle={{
                    background: isDark ? "rgba(30,41,59,0.95)" : "#fff",
                    border: `1px solid ${borderC}`,
                    borderRadius: 12,
                    backdropFilter: "blur(8px)",
                    color: text,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                  }}
                />
                <Bar dataKey="ALP" fill="#E11D48" radius={[4, 4, 0, 0]} name="Labor" />
                <Bar dataKey="Liberal" fill="#2563EB" radius={[4, 4, 0, 0]} name="Liberal" />
                <Bar dataKey="Greens" fill="#10B981" radius={[4, 4, 0, 0]} name="Greens" />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-5 mt-3">
              {[{ color: "#E11D48", label: "Labor" }, { color: "#2563EB", label: "Liberal" }, { color: "#10B981", label: "Greens" }].map((l) => (
                <div key={l.label} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm" style={{ background: l.color }} />
                  <span className="text-xs font-medium" style={{ color: textSec }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            className="mt-4 overflow-hidden"
            style={{ border: `1px solid ${borderC}`, borderRadius: 16 }}
            data-testid="sa-polls-table"
          >
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table className="w-full text-sm" style={{ minWidth: 560 }}>
              <thead>
                <tr style={{ background: isDark ? "#0F172A" : "#F8FAFC", borderBottom: `1px solid ${borderC}` }}>
                  {["Pollster", "Date", "ALP", "Liberal", "Greens", "Others", "ALP 2PP", "Lib 2PP"].map((h) => (
                    <th key={h} className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-wider" style={{ color: textSec }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SA_POLLS.map((p, i) => (
                  <tr key={p.pollster} style={{ background: i % 2 === 0 ? surface : (isDark ? "rgba(15,23,42,0.4)" : "#F8FAFC") }}>
                    <td className="py-2.5 px-3 font-semibold border-b text-xs" style={{ color: text, borderColor: borderC }}>{p.pollster}</td>
                    <td className="py-2.5 px-3 border-b text-xs" style={{ color: textSec, borderColor: borderC }}>{p.date}</td>
                    <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#E11D48", borderColor: borderC }}>{p.alp}%</td>
                    <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#2563EB", borderColor: borderC }}>{p.lib}%</td>
                    <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#10B981", borderColor: borderC }}>{p.grn}%</td>
                    <td className="py-2.5 px-3 border-b text-xs" style={{ color: textSec, borderColor: borderC }}>{p.oth}%</td>
                    <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#E11D48", borderColor: borderC }}>{p.two_pp_alp}%</td>
                    <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#2563EB", borderColor: borderC }}>{p.two_pp_lib}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
          <p className="text-xs mt-2" style={{ color: textSec }}>* SA state polling estimates. Source: Redbridge, Essential, Newspoll SA, Roy Morgan.</p>
        </section>

        {/* ── NEW: Marginal Seats ── */}
        <MarginalSeatsWidget />

        {/* ── NEW: Who Funds This Election ── */}
        <SAFundingWidget />

        {/* How to vote */}
        <section data-testid="how-to-vote-section">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(13,92,58,0.10)", border: "1px solid rgba(13,92,58,0.18)" }}>
              <MapPin style={{ width: 18, height: 18, color: "#10B981" }} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              How to Vote in SA 2026
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: "Check Your Enrolment", icon: CheckCircle, color: "#10B981", body: "Make sure you're enrolled by March 3, 2026. SA rolls close earlier than most states. Check at elections.sa.gov.au", link: "https://www.elections.sa.gov.au", linkText: "Check enrolment →" },
              { title: "Find Your Polling Place", icon: MapPin, color: "#0D5C3A", body: "SA uses Lower House preferential voting and an optional preferential system for the upper house (Legislative Council).", link: "https://www.elections.sa.gov.au", linkText: "Find a polling place →" },
              { title: "Voting System", icon: Clock, color: "#2563EB", body: "Lower house: number ALL boxes 1, 2, 3…\nUpper house: you can vote above or below the line. Both compulsory.", link: "https://www.elections.sa.gov.au/how-to-vote", linkText: "Learn how to vote →" },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="overflow-hidden transition-all duration-200 hover:-translate-y-1"
                  style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd }}
                >
                  <div style={{ height: 2.5, background: `linear-gradient(90deg, ${card.color}, ${card.color}44)` }} />
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ background: `${card.color}10`, border: `1px solid ${card.color}20` }}
                      >
                        <Icon style={{ width: 16, height: 16, color: card.color }} />
                      </div>
                      <h3 className="font-semibold text-sm tracking-tight" style={{ color: text }}>{card.title}</h3>
                    </div>
                    <p className="text-xs mb-3 leading-relaxed whitespace-pre-line" style={{ color: textSec }}>{card.body}</p>
                    <a
                      href={card.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-semibold flex items-center gap-1 transition-all"
                      style={{ color: card.color }}
                    >
                      {card.linkText} <ExternalLink style={{ width: 11, height: 11 }} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Quick Tools */}
        <section data-testid="quick-tools-section">
          <h2 className="text-2xl font-bold mb-5 tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
            Useful Tools for SA Voters
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: "SA Politicians", desc: "Malinauskas, Speirs + full team", path: "/politicians?level=state", color: "#E11D48" },
              { label: "Promise Tracker", desc: "Track what they promised SA", path: "/promises", color: "#0D5C3A" },
              { label: "Promise Leaderboard", desc: "Who delivers on promises?", path: "/leaderboard", color: "#D97706" },
              { label: "Party Match Quiz", desc: "Which SA party matches you?", path: "/quiz", color: "#2563EB" },
            ].map((t) => (
              <button
                key={t.label}
                onClick={() => navigate(t.path)}
                className="text-left transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                style={{
                  background: surface,
                  border: `1px solid ${borderC}`,
                  borderRadius: 14,
                  boxShadow: shadowMd,
                  padding: "16px",
                  cursor: "pointer",
                }}
                data-testid={`tool-${t.label.toLowerCase().replace(/ /g, "-")}`}
              >
                <div
                  className="w-1 h-8 rounded-full mb-3"
                  style={{ background: `linear-gradient(180deg, ${t.color}, ${t.color}55)` }}
                />
                <p className="font-semibold text-sm mb-1 tracking-tight" style={{ color: t.color, fontFamily: "Outfit, sans-serif" }}>{t.label}</p>
                <p className="text-xs" style={{ color: textSec }}>{t.desc}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Official info */}
        <div
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          style={{
            background: isDark ? "rgba(30,41,59,0.6)" : "rgba(234,179,8,0.06)",
            border: `1px solid rgba(234,179,8,0.18)`,
            borderRadius: 16,
            padding: "20px 24px",
          }}
        >
          <div>
            <p className="font-semibold text-sm mb-1" style={{ color: text }}>Official SA Election Authority</p>
            <p className="text-xs" style={{ color: textSec }}>All electoral information, candidate lists, and polling place finder at the Electoral Commission of SA.</p>
          </div>
          <a
            href="https://www.elections.sa.gov.au"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold px-4 py-2.5 rounded-xl transition-all hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(135deg, #E11D48, #F43F5E)",
              color: "#fff",
              boxShadow: "0 4px 14px rgba(225,29,72,0.26)",
              textDecoration: "none",
            }}
            data-testid="official-ecsa-link"
          >
            elections.sa.gov.au <ExternalLink style={{ width: 12, height: 12 }} />
          </a>
        </div>
      </div>
    </div>
  );
}
